/* eslint-disable no-nested-ternary */
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { BENEFITS_URL } from 'src/config-global';
import { AxiosHttpClient } from 'src/http';
import { BenefitsRepository } from 'src/repositories/benefits/benefits.repository';
import { CouponsRepository } from 'src/repositories/benefits/coupons.repository';
import { ProductRepository } from 'src/repositories/products/products.repository';
import { paths } from 'src/routes/paths';
import { useHomeBannersStore } from 'src/store/personalization/home-banners/home-banners.store';
import * as Yup from 'yup';

interface BenefitOption {
  value?: string;
  label?: string;
}

interface ImageField {
  url: string | File;
  redirect: string;
  text: string;
  benefit: BenefitOption;
}

interface HomeBannerFormValues {
  name: string;
  priority: number;
  type: 'one-image' | 'two-images' | 'three-images';
  visibleForTiers?: string[];
  images?: ImageField[];
  status: 'active' | 'inactive';
}


export default function useHomeBannersDetails() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const api = new AxiosHttpClient(BENEFITS_URL);
  const repository = new BenefitsRepository(api);
  const productsRespository = new ProductRepository(api);
  const couponsRespository = new CouponsRepository(api);

  const [benefitsForNewUsers, setBenefitsForNewUsers] = useState<any[]>([]);
  const [selectedBenefits, setSelectedBenefits] = useState<any[]>([]);
  const [initialSelectedBenefits, setInitialSelectedBenefits] = useState<any[]>([]);

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  const TYPE_OPTIONS = ['one-image', 'two-images', 'three-images'];
  const TYPE_LABEL = ['Uma imagem', 'Duas imagens', 'Três imagens'];

  const TIERS_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'silver', label: 'Prata' },
    { value: 'gold', label: 'Ouro' },
    { value: 'diamond', label: 'Diamante' },
  ];

  const {
    homeBannerToEdit,
    isLoadingCreate,
    isLoadingDetails,
    findOne,
    create,
    update,
    resetHomeBannerDetails,
  } = useHomeBannersStore();

  useEffect(() => {
    if (id) {
      findOne(id);
      getBenefitsForNewUsers();
    } else {
      resetHomeBannerDetails();
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (homeBannerToEdit) {
      homeBannerToEdit.images.forEach(async (i) => {
        getSelectedBenefits({ type: i.benefitType, id: i.benefitId })
      })
    }

  }, [homeBannerToEdit])

  const getSelectedBenefits = async (benefit: { type?: string, id?: string }) => {
    if (benefit.id) {
      try {
        let result: any;
        if (benefit.type === 'coupon') {
          const response = await couponsRespository.getCouponDetails(benefit.id)
          result = response.value.coupon
        } else {
          const response = await productsRespository.getProductDetails(benefit.id)
          result = response.value.product
        }
        setInitialSelectedBenefits((prev) => [...prev, { ...result, type: benefit.type }])
        setSelectedBenefits((prev) => [...prev, { ...result, type: benefit.type }])
      } catch (error) {
        console.log(error)
      }
    }
  }

  const defaultValues = useMemo(
    () => ({
      name: homeBannerToEdit?.name || '',
      priority: homeBannerToEdit?.priority || 0,
      type: homeBannerToEdit?.type || 'one-image',
      visibleForTiers:
        homeBannerToEdit?.visibleForTiers?.length === 0
          ? ['all']
          : homeBannerToEdit?.visibleForTiers || [],
      images:
        homeBannerToEdit?.images.map((img) => {
          const benefit = initialSelectedBenefits?.find((b) => b.id === img.benefitId)
          return {
            url: img.url,
            redirect: img.redirect,
            text: img.text,
            benefit: {
              value: img.benefitId,
              label: benefit?.name
                ? `${benefit.name} - ${benefit.type === 'product' ? 'Produto' : 'Cupom'}`
                : ''
            },
          }
        }
        ) || [
          { url: '', redirect: '', text: '', benefit: { label: '', value: '' } },
        ],
      status: homeBannerToEdit?.status || 'active',
    }),
    [isEdit, homeBannerToEdit, initialSelectedBenefits]
  );

  const homeBannerSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    priority: Yup.number().required('Campo obrigatório!'),
    type: Yup.string().required('Campo obrigatório!'),
    visibleForTiers: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    images: Yup.array().of(
      Yup.object().shape({
        url: Yup.mixed().test(
          'fileError',
          'Adicione uma imagem!',
          (v) => v !== ''
        ),
        redirect: Yup.string(),
        text: Yup.string(),
        benefit: Yup.object({
          value: Yup.string().nullable(),
          label: Yup.string().nullable(),
        })
          .nonNullable('Benefício Obrigatório')
          .when('redirect', {
            is: (redirect: string) => redirect?.includes('forNewUsers'),
            then: (schema) =>
              schema.shape({
                value: Yup.string().required('Selecione um benefício!'),
                label: Yup.string().required('Campo obrigatório!'),
              }),
            otherwise: (schema) =>
              schema.shape({
                value: Yup.string().nullable(),
                label: Yup.string().nullable(),
              }),
          }),
      })
    ),
    status: Yup.string().required('Campo obrigatório!'),
  });

  const methods = useForm<any>({
    resolver: yupResolver(homeBannerSchema),
    defaultValues: defaultValues as HomeBannerFormValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { isSubmitting },
  } = methods;

  const type = watch('type');
  const images = watch('images') as ImageField[] | undefined;
  const visibleForTiers = watch('visibleForTiers');

  useEffect(() => {
    if (
      Array.isArray(visibleForTiers) &&
      visibleForTiers.includes('all') &&
      visibleForTiers.length > 1
    ) {
      setValue('visibleForTiers', ['all']);
    }
  }, [visibleForTiers]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [homeBannerToEdit, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      data.visibleForTiers =
        Array.isArray(data.visibleForTiers) &&
          data.visibleForTiers.length === 1 &&
          data.visibleForTiers[0] === 'all'
          ? []
          : data.visibleForTiers;

      const formattedData = {
        ...data,
        images: data.images.map((img: any) => {
          if (img?.benefit?.value) {
            return {
              url: img.url,
              redirect: img.redirect,
              text: img.text,
              benefitId: img?.benefit?.value,
              benefitType: selectedBenefits.find((b) => b.id === img?.benefit?.value).type
            }
          }
          return {
            url: img.url,
            redirect: img.redirect,
            text: img.text,
          }
        })
      }

      if (isEdit && id) {
        formattedData.id = id;
        await update(id, formattedData);
        enqueueSnackbar('Página de banners atualizada com sucesso!');
      } else {
        await create(formattedData);
        enqueueSnackbar('Página de banners criada com sucesso!');
      }

      navigate(paths.dashboard.homeBanner);
    } catch (error) {
      console.error(error);
    }
  });

  const { fields, replace, append, remove } = useFieldArray({
    control,
    name: 'images',
  });

  useEffect(() => {
    const numberOfImages =
      type === 'one-image' ? 1 : type === 'two-images' ? 2 : 3;
    const newFields = Array.from({ length: numberOfImages }, (_, i) =>
      images && images[i]
        ? images[i]
        : { url: '', redirect: '', text: '', benefit: { label: '', value: '' } }
    );
    replace(newFields);
  }, [replace, type]);

  const handleDrop = useCallback(
    (acceptedFiles: any, idx: any) => {
      const file = acceptedFiles[0];
      if (file) {
        setValue(
          `images.${idx}.url`,
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
      }
    },
    [setValue]
  );

  const getBenefitsForNewUsers = async () => {
    try {
      const response = await repository.getBenefitsForNewUsers();
      if (response.isLeft()) {
        throw new Error(response.value.message);
      }
      setBenefitsForNewUsers(response.value);
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: 'Erro ao carregar os benefícios para novos usuários',
      });
    }
  };

  useEffect(() => {
    if (
      images?.some((image) => image.redirect?.includes('forNewUsers')) &&
      benefitsForNewUsers.length === 0
    ) {
      getBenefitsForNewUsers();
    }

    if (images?.some((img) => img.benefit?.value)) {
      images.forEach((i) => {
        if (!selectedBenefits.find((b) => b.id === i.benefit?.value) && benefitsForNewUsers?.length > 0) {
          setSelectedBenefits((prev) => [...prev, { id: i.benefit.value, name: i.benefit.label, visibleForTiers: benefitsForNewUsers.find((b) => i.benefit?.value === b.id)?.tiers, type: benefitsForNewUsers.find((b) => i.benefit?.value === b.id)?.type }])
        }
      })
    }

  }, [JSON.stringify(images)]);

  useEffect(() => {
    if ((images?.some((image) => image.benefit?.value) && selectedBenefits.length > 0) && !visibleForTiers?.includes('all')) {
      images.forEach((img) => {
        if (img.benefit?.value) {
          const benefitTiers = selectedBenefits.find((b) => b.id === img.benefit?.value)?.visibleForTiers ?? []
          if (!benefitTiers?.every((b: string) => visibleForTiers?.includes(b))) {
            const auxBenefitTiers = benefitTiers.filter((t: string) => !visibleForTiers?.includes(t))
            setValue('visibleForTiers', [...(visibleForTiers || []), ...auxBenefitTiers]);
          } else if (benefitTiers?.length === 0) {
            setValue('visibleForTiers', ['all'])
          }
        }
      })
    }
  }, [selectedBenefits, visibleForTiers])

  const selectedBenefitTiers = (index: number) => {
    const tiers = selectedBenefits?.find((b: any) =>
      b.id ===
      images![index]?.benefit?.value)
      ?.visibleForTiers
    if (tiers?.length > 0 && tiers?.length < 3) {
      return tiers.map((t: string) => {
        if (t === 'silver') {
          return 'Prata'
        }
        if (t === 'gold') {
          return 'Ouro'
        }
        return 'Diamante'
      }).join(', ')
    }
    return 'Todos os Tiers'
  }

  return {
    id,
    isLoadingCreate,
    isEdit,
    methods,
    fields,
    isSubmitting,
    control,
    isLoadingDetails,
    STATUS_OPTIONS,
    STATUS_LABEL,
    TYPE_OPTIONS,
    TYPE_LABEL,
    TIERS_OPTIONS,
    type,
    benefitsForNewUsers,
    selectedBenefits,
    images,
    selectedBenefitTiers,
    handleDrop,
    append,
    remove,
    onSubmit,
  };
}
